// TODO[sdk-realtime]: SDK realtime for chat is not yet implemented. Keep the
// direct supabase realtime channel here until the SDK exposes equivalent
// `workspaceProject.chat.*` subscribe routes — at that point migrate every
// `channel.on('postgres_changes', ...)` block below to
// `getSDK().execute('workspaceProject.chat.<table>', 'subscribe', filter, cb)`.
//
// Wires the supabase realtime channel to root state. Called once from
// pages/home.js onCreate; cancelled in onRemove.
//
// Three subscriptions:
//   1. postgres_changes on chat_messages → push into messagesByChannel
//   2. postgres_changes on chat_channels + chat_channel_members → reload lists
//   3. presence channel keyed by currentUserEmail → drives root.presence
import { getRealtimeSupabase } from '../supabaseClient.js'
import { rowToMessage, rowToChannel } from './chatUtils.js'
import { createNotification } from '../notifications/createNotification.js'
import { isChannelMuted } from './muteChannel.js'

export const subscribeRealtime = async function () {
  const root = this.state.root
  const me = root.currentUserEmail

  // Guard: if already subscribed (e.g. navigated away and back), reuse.
  if (root._chatRealtimeChannel) return root._chatRealtimeChannel

  const sb = await getRealtimeSupabase()
  const channel = sb.channel('governance-chat', {
    config: { presence: { key: me } }
  })

  channel.on('postgres_changes',
    { event: 'INSERT', schema: 'public', table: 'chat_messages' },
    (payload) => {
      const msg = rowToMessage(payload.new)
      const list = root.messagesByChannel[msg.channelId] || []
      // Skip if we already have it (optimistic id was reconciled in sendMessage).
      if (list.some(m => m.id === msg.id)) return
      root.update({
        messagesByChannel: { ...root.messagesByChannel, [msg.channelId]: [...list, msg] }
      })
    }
  )

  channel.on('postgres_changes',
    { event: 'UPDATE', schema: 'public', table: 'chat_messages' },
    (payload) => {
      const msg = rowToMessage(payload.new)
      const list = (root.messagesByChannel[msg.channelId] || [])
        .map(m => m.id === msg.id ? msg : m)
      root.update({
        messagesByChannel: { ...root.messagesByChannel, [msg.channelId]: list }
      })
    }
  )

  channel.on('postgres_changes',
    { event: '*', schema: 'public', table: 'chat_channels' },
    (payload) => {
      const next = rowToChannel(payload.new || payload.old)
      // Read live state — `root.channels` from the closure may be stale.
      const current = root.channels || []
      const exists = current.find(c => c.id === next.id)
      let channels = current
      if (payload.eventType === 'DELETE') {
        channels = current.filter(c => c.id !== next.id)
      } else if (exists) {
        channels = current.map(c => c.id === next.id ? next : c)
      } else {
        channels = [...current, next]
      }
      // Only update if the array actually changed.
      if (channels !== current) root.update({ channels })
    }
  )

  channel.on('postgres_changes',
    { event: '*', schema: 'public', table: 'chat_channel_members' },
    (payload) => {
      // Apply the delta in-place — full refetch on every membership flip
      // (e.g. every `markRead` write) was downloading the entire join table.
      const cur = root.channelMembers || []
      const isSameRow = (a, b) => a.channel_id === b.channel_id && a.member_email === b.member_email
      let next = cur
      if (payload.eventType === 'INSERT') {
        if (!cur.some(r => isSameRow(r, payload.new))) next = [...cur, payload.new]
      } else if (payload.eventType === 'UPDATE') {
        next = cur.map(r => isSameRow(r, payload.new) ? payload.new : r)
      } else if (payload.eventType === 'DELETE') {
        next = cur.filter(r => !isSameRow(r, payload.old))
      }
      if (next !== cur) root.update({ channelMembers: next })
    }
  )

  // chat_mentions: append rows that mention me, drive the sidebar badge.
  channel.on('postgres_changes',
    { event: 'INSERT', schema: 'public', table: 'chat_mentions' },
    (payload) => {
      const row = payload.new
      if (!row || row.mentioned_email !== me) return
      const cur = root.myMentions || []
      if (cur.some(x => x.id === row.id)) return
      root.update({ myMentions: [...cur, row] })
      // Skip notification if the viewer muted this conversation.
      if (isChannelMuted(root, row.channel_id)) return
      // Create a notification for the mention
      const msg = (root.messagesByChannel || {})[row.channel_id]?.find(m => m.id === row.message_id)
      const senderName = msg?.authorName || msg?.author_email || 'Someone'
      const channelName = (root.channels || []).find(c => c.id === row.channel_id)?.name || 'a channel'
      createNotification.call({ state: { root } }, {
        userEmail: me,
        type: 'mention',
        title: `${senderName} mentioned you in #${channelName}`,
        body: msg?.body?.substring(0, 100) || '',
        href: `/chat#channel=${row.channel_id}`
      })
    }
  )

  channel.on('presence', { event: 'sync' }, () => {
    const presenceState = channel.presenceState()
    const next = {}
    for (const key of Object.keys(presenceState)) {
      const meta = presenceState[key][0] || {}
      next[key] = { status: meta.status || 'online', last_seen_at: meta.online_at || new Date().toISOString() }
    }
    root.update({ presence: next })
  })

  channel.subscribe(async (status) => {
    if (status !== 'SUBSCRIBED') return
    await channel.track({ status: 'online', online_at: new Date().toISOString() })
  })

  root._chatRealtimeChannel = channel
  return channel
}
